/* ============================================================
   Cart policy consent gate  (MOOEi / Luxe)

   Shoppers must tick "I have read and agree..." before checkout, and the
   checkbox itself only unlocks once every policy has been opened. Policies
   open in a modal on the cart rather than navigating away, so the cart is
   never left behind.

   Three things drive the design:

   1. Both cart surfaces can reach checkout independently -- the cart page and
      the drawer -- so every checkout control is gated, not just the one on the
      page the shopper happens to be looking at.

   2. cart.js and cart-drawer.js replace whole sections with innerHTML on every
      cart change, which destroys this UI and re-creates it from server HTML.
      So: no per-element listeners that would be lost, only delegation on
      document, plus a MutationObserver that re-applies state after a re-render.
      It also means the tick has to be remembered outside the DOM, or adding an
      item would silently un-tick the box. The modal is appended to <body> for
      the same reason -- inside a cart section it would be torn out mid-read.

   3. Fail open. The buttons are enabled in markup and disabled here on load.
      If this script fails to load or throws, checkout still works -- a broken
      consent gate must not become a broken store. The policy links keep their
      real href and target="_blank" for the same reason.
   ============================================================ */
(function () {
  'use strict';

  var OPENED_KEY = 'cpc:opened';    // localStorage - which policies were read
  var AGREED_KEY = 'cpc:agreed';    // sessionStorage - ticked this visit

  var CHECKOUT_BUTTONS = '#checkout, #CartDrawer-Checkout, [name="checkout"]';
  var DYNAMIC_WRAP = '.cart__dynamic-checkout-buttons';
  var LABEL = { shipping: 'Shipping Policy', refund: 'Refund Policy' };

  /* Storage can throw in private mode / when cookies are blocked. Every access
     is guarded so a storage failure degrades to "not opened, not agreed"
     rather than breaking the page. */
  function readJSON(store, key) {
    try { return JSON.parse(window[store].getItem(key)) || {}; } catch (e) { return {}; }
  }
  function writeJSON(store, key, val) {
    try { window[store].setItem(key, JSON.stringify(val)); } catch (e) {}
  }
  function agreed() {
    try { return window.sessionStorage.getItem(AGREED_KEY) === '1'; } catch (e) { return false; }
  }
  function setAgreed(v) {
    try { window.sessionStorage.setItem(AGREED_KEY, v ? '1' : '0'); } catch (e) {}
  }

  function requiredFor(block) {
    return (block.getAttribute('data-cpc-required') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }
  function missingPolicies(block) {
    var opened = readJSON('localStorage', OPENED_KEY);
    return requiredFor(block).filter(function (p) { return !opened[p]; });
  }
  function markOpened(policy) {
    var opened = readJSON('localStorage', OPENED_KEY);
    opened[policy] = true;
    writeJSON('localStorage', OPENED_KEY, opened);
    sync();
  }

  /* ---------------------------------------------------------- state ------ */

  function sync() {
    var blocks = document.querySelectorAll('[data-cpc]');
    if (!blocks.length) return;

    var allUnlocked = true;
    var isAgreed = agreed();

    blocks.forEach(function (block) {
      var missing = missingPolicies(block);
      var box = block.querySelector('[data-cpc-check]');
      var hint = block.querySelector('[data-cpc-hint]');

      if (box) {
        box.disabled = missing.length > 0;
        box.checked = isAgreed && missing.length === 0;
      }
      if (missing.length) allUnlocked = false;

      if (hint) {
        if (missing.length) {
          hint.textContent = 'Open the ' +
            missing.map(function (p) { return LABEL[p] || p; }).join(' and ') +
            ' above to enable this.';
          hint.hidden = false;
        } else if (!isAgreed) {
          hint.textContent = 'Tick the box to continue to checkout.';
          hint.hidden = false;
        } else {
          hint.textContent = '';
          hint.hidden = true;
        }
      }
      block.classList.toggle('cpc--locked', missing.length > 0);
    });

    var pass = isAgreed && allUnlocked;

    document.querySelectorAll(CHECKOUT_BUTTONS).forEach(function (btn) {
      /* A button we have not touched yet is exactly as the theme rendered it.
         If the theme already disabled it, the cart is empty -- leave it alone,
         or consenting would switch checkout back on for an empty cart. Every
         cart re-render produces fresh buttons, so this is re-evaluated each
         time rather than recorded once at load. */
      if (!btn.hasAttribute('data-cpc-managed')) {
        if (btn.disabled) return;
        btn.setAttribute('data-cpc-managed', '');
      }
      btn.disabled = !pass;
      btn.setAttribute('aria-disabled', String(!pass));
    });

    /* Accelerated checkout (Apple Pay / Shop Pay / PayPal) posts straight to
       Shopify without touching the cart form, so covering it is the only way
       to gate it. */
    document.querySelectorAll(DYNAMIC_WRAP).forEach(function (wrap) {
      wrap.classList.toggle('cpc-blocked', !pass);
      wrap.setAttribute('aria-hidden', String(!pass));
    });
  }

  /* ---------------------------------------------------------- modal ------ */

  var modal = null;
  var lastFocus = null;
  var cache = {};

  function buildModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'cpc-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="cpc-modal__backdrop" data-cpc-close></div>' +
      '<div class="cpc-modal__panel" role="dialog" aria-modal="true" aria-labelledby="cpc-modal-title" tabindex="-1">' +
        '<h2 class="cpc-modal__title" id="cpc-modal-title"></h2>' +
        '<div class="cpc-modal__body" tabindex="0"></div>' +
        '<div class="cpc-modal__foot">' +
          '<button type="button" class="cpc-modal__close" data-cpc-close>Close</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    return modal;
  }

  function openModal(policy, title, url) {
    var m = buildModal();
    lastFocus = document.activeElement;
    m.querySelector('.cpc-modal__title').textContent = title;
    var body = m.querySelector('.cpc-modal__body');
    m.hidden = false;
    document.documentElement.classList.add('cpc-modal-open');
    m.querySelector('.cpc-modal__panel').focus();

    /* opening it counts as reading it -- do this immediately rather than on a
       successful fetch, so a network hiccup cannot trap the shopper */
    markOpened(policy);

    if (cache[url]) { body.innerHTML = cache[url]; return; }

    /* The policy text is inlined by snippets/cart-policy-data.liquid, so the
       usual path needs no request at all. The fetch below is only a fallback
       for the case where that snippet is missing. */
    var inline = document.getElementById('cpc-policy-' + policy);
    if (inline) {
      cache[url] = inline.innerHTML;
      body.innerHTML = cache[url];
      body.scrollTop = 0;
      return;
    }

    body.innerHTML = '<p class="cpc-modal__loading">Loading…</p>';

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('.shopify-policy__body') ||
                      doc.querySelector('.shopify-policy__container') ||
                      doc.querySelector('main');
        if (!content) throw new Error('policy body not found');
        cache[url] = content.innerHTML;
        body.innerHTML = cache[url];
        body.scrollTop = 0;
      })
      .catch(function () {
        /* never dead-end: offer the real page instead */
        body.innerHTML = '<p>We could not load this here. ' +
          '<a href="' + url + '" target="_blank" rel="noopener">Open the full policy</a>.</p>';
      });
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('cpc-modal-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* --- delegated events: survive every cart re-render --- */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cpc-close]')) { closeModal(); return; }

    var link = e.target.closest('[data-cpc-policy]');
    if (!link) return;
    var url = link.getAttribute('href');
    if (!url) return;
    e.preventDefault();
    openModal(link.getAttribute('data-cpc-policy'),
              (link.textContent || 'Policy').trim(), url);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  document.addEventListener('change', function (e) {
    var box = e.target.closest('[data-cpc-check]');
    if (!box) return;
    setAgreed(box.checked);
    sync();
  });

  /* Belt and braces: if a checkout control is reached some other way (keyboard,
     an app, a stale enabled button), stop the submit and say why. */
  function blockIfNotConsented(e) {
    var trigger = e.target.closest
      ? (e.target.closest('[name="checkout"]') || e.target.closest('#checkout, #CartDrawer-Checkout'))
      : null;
    if (!trigger) return;
    var blocks = document.querySelectorAll('[data-cpc]');
    if (!blocks.length) return;
    var ok = agreed() && Array.prototype.every.call(blocks, function (b) {
      return missingPolicies(b).length === 0;
    });
    if (ok) return;
    e.preventDefault();
    e.stopPropagation();
    var block = document.querySelector('[data-cpc]');
    if (block) {
      block.classList.add('cpc--flash');
      setTimeout(function () { block.classList.remove('cpc--flash'); }, 1200);
      var box = block.querySelector('[data-cpc-check]');
      if (box && !box.disabled) box.focus();
      block.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }
  document.addEventListener('click', blockIfNotConsented, true);
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    if (!form.querySelector('[name="checkout"]')) return;
    blockIfNotConsented({
      target: form.querySelector('[name="checkout"]'),
      preventDefault: function () { e.preventDefault(); },
      stopPropagation: function () { e.stopPropagation(); }
    });
  }, true);

  /* --- re-apply after the cart sections are swapped out --- */
  var pending = null;
  var observer = new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () { pending = null; sync(); }, 60);
  });

  function start() {
    sync();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
